import { ToolbarEventManagerHelper } from './../../../services/event-managers/toolbar-event-manager.helper';
import { Component, ChangeDetectionStrategy, ChangeDetectorRef, OnInit, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, filter, of, takeUntil } from 'rxjs';
import { catchError, defaultIfEmpty, take } from 'rxjs/operators';
import { BaseComponent } from '../../base/base.component';
import { LoggerService } from '../../../services/logger.service';
import { UserService } from '../../../services/user.service';
import { WorkspaceStore } from '../../../services/stores/workspace.store';
import { NodeHelper } from '../../../services/helpers/node.helper';
import { UserStore } from '../../../services/stores/user.store';
import { ExternalLinkStore } from '../../../services/stores/external-link.store';
import { CatchHubLogModel } from '../../../models/catchHubLog/catchHubLog.model';
import { CatchHubEventType, CatchHubLogType } from '../../../models/catchHubLog/catchHubLog.enum';
import { IntelliCatchMessageAction } from '../../../models/copilot/intellicatch-message.enum';
import * as RouteConstants from '../../../modules/catch-core/data/constants/route.constants';
import * as BluePrintConstants from '../../../modules/catch-core/data/constants/blueprint.constants';
import * as UiMessageConstants from '../../../modules/catch-core/data/constants/ui-message.constants';
import { CATCH_INTERNAL_APP_INTELLICATCH_LOWER } from '../../../modules/catch-core/data/constants/app.constants';
import { BLANK_FRAME_URL, CHAT_IFRAME_SELECTOR, IIntelliCatchItemContext } from '../../../models/copilot/intellicatch-item-context.model';
import { IntelliCatchTestCreateOutcome } from '../../../models/copilot/intellicatch-create.enum';
import {
  IIntelliCatchCreateAvailabilityPayload,
  IIntelliCatchCreateReport,
  IIntelliCatchCreateResultPayload,
  IIntelliCatchTestCreateRequest,
  IIntelliCatchTestCreateResult,
  IIntelliCatchTestStepInput,
  IIntelliCatchTestSuggestion,
  IIntelliCatchUnlinkedCreateResult,
} from '../../../models/copilot/intellicatch-create.model';
import { IIntelliCatchAvailability, IntelliCatchAvailabilityState } from '../../../models/copilot/intellicatch-availability.enum';
import { IntelliCatchItemContextService } from '../../../services/intellicatch-item-context.service';
import { IntelliCatchAvailabilityHelper } from '../../../services/helpers/intellicatch-availability.helper';
import { IntelliCatchLinkedTestHelper } from '../../../services/helpers/intellicatch-linked-test.helper';
import { IntelliCatchLogHelper } from '../../../services/helpers/intellicatch-log.helper';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { CatchHubLogStore } from '../../../services/stores/catchHubLog.store';
import { ProjectStore } from '../../../services/stores/projects.store';
import { IdHelper } from '../../../services/helpers/id.helper';
import { UserModel } from '../../../models/user/user.model';
import { ProjectService } from '../../../services/project.service';
import { UserDataHelper } from '../../../services/helpers/user-data.helper';
import { BlueprintStore } from '../../../services/stores/blueprint.store';
import { EditPanelTestStepHelperService } from '../../../modules/shared-edit-panel/services/edit-panel-test-step.helper';
import { CopilotAuthService } from '../../../services/copilot-auth.service';
import { ExternalLinkService } from '../../../services/api/external-link.api.service';
import { AiProvider } from '../../../models/child-application/ai-provider.enum';
import { ITestTableRowModel } from '../../../models/test-step/test-table-row.model';
import { environment } from '../../../../environments/environment';

/**
 * How long the sign-in banner waits before offering the user a way out. Shorter than MSAL's own
 * popup timeout, because a popup closed before it reaches the redirect bridge never broadcasts
 * anything and MSAL simply waits - which would otherwise leave the button dead with no explanation.
 *
 * This wait is deliberately generous, and is the only thing that releases the button early: the
 * popup is navigated straight to login.microsoftonline.com, so its window handle is cross-origin
 * and its `closed` flag cannot be trusted to mean the user closed it. Reporting a cancellation off
 * that flag mislabelled a real sign-in as cancelled while the user was still approving MFA.
 */
const COPILOT_SIGN_IN_WATCHDOG_MS = 45000;

/** Long enough to read a sentence that names what was and was not created. */
const CREATE_RESULT_SNACK_BAR_MS = 5000;

/** The header chat's creates are loose: no item to hang them off, so no parent is named. */
const NO_PARENT_NODE_ID = '';

@Component({
  selector: 'app-chat-window',
  templateUrl: './chat-window.component.html',
  styleUrls: ['./chat-window.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  animations: [
    trigger('enterAnimation', [
      transition(':enter', [style({ transform: 'translateX(-100%)', opacity: 0 }), animate('450ms', style({ transform: 'translateX(0)', opacity: 1 }))]),
      transition(':leave', [style({ transform: 'translateX(0)', opacity: 1 }), animate('450ms', style({ transform: 'translateX(-100%)', opacity: 0 }))]),
    ]),
    trigger('chatbotAnimation', [
      state(
        'visible',
        style({
          opacity: 1,
          transform: 'translateY(0)',
        }),
      ),
      state(
        'hidden',
        style({
          opacity: 0,
          transform: 'translateY(20px)',
          pointerEvents: 'none',
        }),
      ),
      transition('hidden => visible', animate('200ms ease-out')),
      transition('visible => hidden', animate('200ms ease-in')),
    ]),
  ],
})
export class ChatWindowComponent extends BaseComponent implements OnInit, OnDestroy {
  public chatbotPosition = {
    x: Math.max(0, window.innerWidth - 470),
    y: Math.max(0, window.innerHeight - 720),
  };

  public isChatbotMaximized = false;
  public isChatbotVisible = false;

  /** True only when this org has an IntelliCatch app that is active and has a URL. */
  private isIntelliCatchAvailable = false;

  /**
   * Origin of the configured IntelliCatch app. The only origin whose messages are acted on: these
   * messages create linked items, so a page that happens to hold a handle to this window must not
   * be able to drive that by posting one.
   */
  private intelliCatchOrigin = '';
  public chatbotUrl: SafeResourceUrl;
  public requiresCopilotSignIn = false;
  public copilotSignInBusy = false;
  public copilotSignInError = '';

  /** Distinguishes sign-in attempts so a stale result cannot overwrite a newer one. */
  private copilotSignInAttempt = 0;

  private organizationId: string = '';
  /** The organization the widget's current document was last told about; a fresh document starts with none. */
  private initializedOrganizationId = '';
  private currentUserId: string = '';
  private assignableProjectUsers: UserModel[] = [];
  private assignableProjectUsersProjectId: string | null = null;
  private className = 'ChatWindowComponent';
  private readonly destroyed = new Subject<void>();
  private boundHandleChatbotMessage: (event: MessageEvent) => void;
  private watchedChatIframe: HTMLIFrameElement | null = null;
  private refreshTimer: any = null;
  private isDragging = false;
  private dragOffset = { x: 0, y: 0 };
  private chatbotAutoCloseRoutes = RouteConstants.CHATBOT_AUTO_CLOSE_ROUTES_ARRAY;

  constructor(
    private userService: UserService,
    private router: Router,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private workspaceStore: WorkspaceStore,
    private loggerService: LoggerService,
    private sanitizer: DomSanitizer,
    private nodeHelper: NodeHelper,
    private userStore: UserStore,
    private catchHubLogStore: CatchHubLogStore,
    private projectStore: ProjectStore,
    private idHelper: IdHelper,
    private toolbarEventManagerHelper: ToolbarEventManagerHelper,
    private externalLinkStore: ExternalLinkStore,
    private blueprintStore: BlueprintStore,
    private copilotAuthService: CopilotAuthService,
    private externalLinkService: ExternalLinkService,
    private projectService: ProjectService,
    private userDataHelper: UserDataHelper,
    private editPanelTestStepHelper: EditPanelTestStepHelperService,
    private intelliCatchItemContextService: IntelliCatchItemContextService,
    private intelliCatchAvailabilityHelper: IntelliCatchAvailabilityHelper,
    private intelliCatchLinkedTestHelper: IntelliCatchLinkedTestHelper,
    private intelliCatchLogHelper: IntelliCatchLogHelper,
  ) {
    super();
    this.chatbotUrl = this.sanitizer.bypassSecurityTrustResourceUrl('');
    this.boundHandleChatbotMessage = this.handleChatbotMessage.bind(this);
    window.addEventListener('message', this.boundHandleChatbotMessage, false);

    this.router.events.pipe(filter((event: any) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => this.handleRouteChange(event.url));
  }

  ngOnInit(): void {
    window.addEventListener('resize', this.handleWindowResize);
    const savedPosition = localStorage.getItem('chatbotPosition');
    if (savedPosition) {
      try {
        this.chatbotPosition = JSON.parse(savedPosition);
      } catch (e) {
        // Use default position if parsing fails
      }
    }

    // Subscribe to user info
    this.subscribers.myUserInfo = this.userStore.$myUserInfo.subscribe((userInfo) => {
      if (userInfo) {
        this.currentUserId = userInfo.id;
      }
    });

    // "Assigned To" candidates come from project membership, not the org-wide user directory -
    // most roles don't hold user:read (see UserStore.loadInitialData), so relying on it would leave
    // IntelliCatch-created items Unassigned for anyone but a hub_admin. Mirrors NodeFactory and
    // edit-panel-container.component.ts's constructUserField, which solve the same problem.
    this.subscribers.assignableProjectUsers = this.projectStore.$selectedProject.subscribe((project) => {
      const projectId = project?.id || null;
      if (projectId === this.assignableProjectUsersProjectId) {
        return;
      }
      this.assignableProjectUsersProjectId = projectId;
      this.postCreateAvailabilityIfOpen();

      if (!projectId) {
        this.assignableProjectUsers = [];
        return;
      }

      this.projectService.getProjectMembers(projectId).subscribe({
        next: (members) => {
          if (projectId !== this.projectStore.selectedProject?.id) {
            return;
          }
          const mappedUsers = (members || []).map((member) => new UserModel(member));
          this.assignableProjectUsers = this.userDataHelper.removeCatchAdminUsers(mappedUsers);
        },
        error: (err: any) => {
          console.error('ChatWindowComponent: GetProjectMembers failed', err);
          this.assignableProjectUsersProjectId = null;
        },
      });
    });

    // Subscribe to selected workspace
    this.subscribers.selectedWorkspace = this.workspaceStore.$selectedWorkspaceInstance.subscribe((data) => {
      if (data?.bluePrintId) {
        this.blueprintStore.ensureBlueprintLoaded(data.bluePrintId);
      }

      this.initializeWorkspaceContext(data);
    });

    this.subscribers.blueprintLoaded = this.blueprintStore.$selectedBluePrint.subscribe((blueprint) => {
      const workspace = this.workspaceStore.selectedWorkspaceInstance;
      if (!workspace?.bluePrintId || !blueprint || blueprint.id !== workspace.bluePrintId) {
        return;
      }

      this.initializeWorkspaceContext(workspace);
    });

    this.subscribers.allBlueprints = this.blueprintStore.$allBluePrints.subscribe(() => {
      const workspace = this.workspaceStore.selectedWorkspaceInstance;
      if (!workspace?.bluePrintId) {
        return;
      }

      this.initializeWorkspaceContext(workspace);
    });

    this.toolbarEventManagerHelper.$CommonToolbarIntelliCatchWindowOpen.subscribe((value) => {
      this.isChatbotVisible = value;
      this.cdr.detectChanges();
      if (value) {
        this.postCreateAvailability();
      }
    });

    // Subscribe to external links to get the chatbot URL
    this.subscribers.externalLinks = this.externalLinkStore.$firstExternalLink.subscribe((externalLink) => {
      // Recomputed on every emission, so switching to an org without IntelliCatch clears the flag
      // rather than leaving the previous org's value behind.
      const intelliCatchLink = externalLink?.externalLinks?.find((link) => link?.productType?.toLowerCase() === CATCH_INTERNAL_APP_INTELLICATCH_LOWER);
      const url = intelliCatchLink?.active ? intelliCatchLink?.url : '';
      this.isIntelliCatchAvailable = !!url;
      this.intelliCatchOrigin = this.toOrigin(url);
      if (url) {
        this.chatbotUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
        this.cdr.detectChanges();
      }
    });
  }

  private initializeWorkspaceContext(workspace: any): void {
    if (!workspace?.bluePrintId) {
      return;
    }

    const blueprint = this.blueprintStore.getBlueprintByIdFromCache(workspace.bluePrintId);
    if (!this.blueprintStore.isBlueprintHydrated(blueprint)) {
      this.blueprintStore.ensureBlueprintLoaded(workspace.bluePrintId);
      return;
    }

    const currentUser = this.userService.getCurrentUser();
    this.organizationId = currentUser?.organizationId ?? workspace.organizationId ?? '';
    if (this.organizationId) {
      this.sendOrganizationIdToChatbot();
    }

    // The blueprint decides whether a Test can exist here at all, so a template that has just
    // finished loading, or one swapped for another, is a fresh answer the widget has to be told.
    this.postCreateAvailabilityIfOpen();
  }

  ngOnDestroy(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    this.destroyed.next();
    this.destroyed.complete();

    super.ngOnDestroy();
    window.removeEventListener('message', this.boundHandleChatbotMessage);
    window.removeEventListener('resize', this.handleWindowResize);
    this.watchedChatIframe?.removeEventListener('load', this.resendOnChatIframeLoad);
    this.watchedChatIframe = null;

    if (this.subscribers) {
      Object.keys(this.subscribers).forEach((key) => {
        if (this.subscribers[key]) {
          this.subscribers[key].unsubscribe();
        }
      });
    }
  }

  public onDragStart(event: MouseEvent): void {
    // Allow dragging from the header area or its children
    const target = event.target as HTMLElement;
    const dragHandle = target.closest('.chat-drag-handle');

    if (!dragHandle) {
      return;
    }

    this.isDragging = true;
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.dragOffset = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };

    document.addEventListener('mousemove', this.onDragMove);
    document.addEventListener('mouseup', this.onDragEnd);
    event.preventDefault();
  }

  public onMinimizeChatbot(): void {
    this.isChatbotMaximized = false;
    // Reset position when minimizing from maximized state
    if (this.isChatbotMaximized) {
      const savedPosition = localStorage.getItem('chatbotPosition');
      if (savedPosition) {
        try {
          this.chatbotPosition = JSON.parse(savedPosition);
        } catch (e) {
          this.chatbotPosition = { x: window.innerWidth - 470, y: window.innerHeight - 720 };
        }
      }
    }

    this.cdr.detectChanges();
  }

  private onDragMove = (event: MouseEvent): void => {
    if (!this.isDragging) return;

    const newX = event.clientX - this.dragOffset.x;
    const newY = event.clientY - this.dragOffset.y;

    // Constrain to window bounds
    const maxX = window.innerWidth - 450; // chatbot width
    const maxY = window.innerHeight - 700;

    this.chatbotPosition = {
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, window.innerHeight - 100)), // Changed: allow dragging closer to bottom
    };

    this.cdr.detectChanges();
  };

  private onDragEnd = (): void => {
    this.isDragging = false;
    document.removeEventListener('mousemove', this.onDragMove);
    document.removeEventListener('mouseup', this.onDragEnd);

    // Optional: Save position to localStorage
    localStorage.setItem('chatbotPosition', JSON.stringify(this.chatbotPosition));
  };

  private handleRouteChange(url: string): void {
    if (!this.isChatbotVisible) {
      return;
    }

    const shouldCloseChatbot = this.chatbotAutoCloseRoutes.some((route) => {
      // Check if it's a main panel route
      if (RouteConstants.MAIN_PANEL_ROUTES.includes(route as any)) {
        return url.includes(`${RouteConstants.OUTLET_NAMES.MAIN_PANEL}:${route}`) || url.includes(`/${route}`);
      } else {
        // Tab panel routes
        return url.includes(`${RouteConstants.OUTLET_NAMES.TAB_PANEL}:${route}`) || url.includes(`/${route}`);
      }
    });

    if (shouldCloseChatbot) {
      this.loggerService.traceInfo(this.className, 'handleRouteChange', 'Auto-closing chatbot due to navigation to project/tab view', '', { url });

      this.closeChatbot();
    }
  }

  private handleChatbotMessage(event: MessageEvent): void {
    if (!this.isMessageFromChatWidget(event)) {
      return;
    }

    const data = event.data;
    if (!data || typeof data !== 'object') {
      return;
    }

    // Handle chatbot visibility toggles (existing code unchanged)
    if (data && data.action === 'toggleChatbot') {
      // this.isChatbotVisible = data.visible;
      this.toolbarEventManagerHelper.CommonToolbarIntelliCatchWindowOpen = data.visible;
      if (!data.visible) {
        this.isChatbotMaximized = false;
        this.chatbotPosition = {
          x: window.innerWidth - 470,
          y: window.innerHeight - 720,
        };
        localStorage.removeItem('chatbotPosition');
      }
      this.cdr.detectChanges();
      return;
    }

    if (data && data.action === 'maximizeChatbot') {
      this.isChatbotMaximized = !this.isChatbotMaximized;
      this.cdr.detectChanges();
      return;
    }

    // Microsoft turned a request away over the customer's own Copilot capacity or licensing. The
    // widget has already told the user; recording it here is what lets an administrator see that it
    // is happening, and to whom, without the user having to report it.
    if (data.action === IntelliCatchMessageAction.CopilotLimit) {
      this.logCopilotLimit(data.payload || {});
      return;
    }

    // The service refused a chat request over its own missing configuration (env vars, an API
    // key, connectivity settings) rather than anything the user or their tenant did. The widget
    // only shows this in the chat bubble; recording it here is what lets an administrator debug it.
    if (data.action === IntelliCatchMessageAction.ServiceError) {
      this.logServiceConfigurationError(data.payload || {});
      return;
    }

    // MSAL lives here, not in the widget, so account changes are requested from the iframe and
    // carried out on this side. Both paths post a token back only if a new sign-in succeeds; the
    // widget has already cleared what it held, so a cancelled switch simply leaves it signed out.
    // Show the sign-in banner FIRST and only then talk to Microsoft. The widget has already
    // discarded its token by the time this message arrives, so the app is signed out from this
    // moment whatever the popup does - and a popup that is blocked, closed, or simply left open
    // must not be able to strand the user in a widget that accepts input it can no longer send.
    if (data.action === 'intellicatch:copilotSignOut') {
      this.applyCopilotSignInState(null);
      this.copilotAuthService
        .signOut()
        .catch((err) => console.error('Copilot sign out failed', err));      return;
    }

    // Action strings arrive over postMessage from the widget, so they are compared case-insensitively.
    const action = String(data?.action ?? '').toLowerCase();

    // The widget gave up generating a reply, after any resends. It has already told the user; the
    // item the reply was for rides beside the payload, and is looked up here because only this side
    // knows the label and project it lives in.
    if (action === IntelliCatchMessageAction.GenerationFailed.toLowerCase()) {
      this.intelliCatchLogHelper.logGenerationFailure(String(data.sessionId ?? ''), data.payload);
      return;
    }

    if (action === IntelliCatchMessageAction.CreateNode.toLowerCase()) {
      this.handleCreateNodeMessage(data);
      return;
    }

    if (action === IntelliCatchMessageAction.CreateNodesBulk.toLowerCase()) {
      this.handleCreateNodesBulkMessage(data);
      return;
    }

    if (action === IntelliCatchMessageAction.GenerationState.toLowerCase()) {
      this.intelliCatchItemContextService.applyGenerationState(String(data.sessionId ?? ''), data.isGenerating === true);
      return;
    }

    if (action === IntelliCatchMessageAction.AttachmentChanged.toLowerCase()) {
      this.intelliCatchItemContextService.applyWidgetAttachment(String(data.sessionId ?? ''));
      return;
    }
  }

  /**
   * A create carries the session of the answer it came from at the top level, beside `action`. Blank
   * means a general answer, which has no item and creates a loose Test exactly as it always has;
   * anything else names the item that answer was generated for, whether or not the chat is still
   * attached to it.
   *
   * `requestId` is the widget's own name for the click, echoed back on the result so it knows which
   * suggestions the answer covers.
   */
  private handleCreateNodeMessage(data: any): void {
    const payload = data?.payload || {};
    const requestId = String(data?.requestId || '').trim();
    const suggestion = this.toTestSuggestion(payload);
    if (!suggestion) {
      this.loggerService.traceInfo(this.className, this.handleCreateNodeMessage.name, 'Invalid createNode payload received', '', payload);
      this.reportCreateOutcome(requestId, { ok: false, isRefusal: true, message: UiMessageConstants.INTELLICATCH_CREATE_NOTHING_TO_CREATE }, String(data?.sessionId || ''));
      return;
    }

    this.createSuggestedTests([suggestion], data, requestId);
  }

  private handleCreateNodesBulkMessage(data: any): void {
    const items: any[] = Array.isArray(data?.payload) ? data.payload : [];
    const requestId = String(data?.requestId || '').trim();
    const suggestions = items.map((item) => this.toTestSuggestion(item)).filter((suggestion): suggestion is IIntelliCatchTestSuggestion => !!suggestion);
    if (suggestions.length === 0) {
      this.reportCreateOutcome(requestId, { ok: false, isRefusal: true, message: UiMessageConstants.INTELLICATCH_CREATE_NOTHING_TO_CREATE }, String(data?.sessionId || ''));
      return;
    }

    this.createSuggestedTests(suggestions, data, requestId);
  }

  /**
   * The refusals every create goes through before anything is created: a project whose template
   * cannot hold a Test, an answer whose item session this page does not know, and an item in a
   * label or project the user has left. Each is answered to the widget as well as shown here, so the
   * suggestion goes back to uncreated.
   */
  private createSuggestedTests(suggestions: IIntelliCatchTestSuggestion[], data: any, requestId: string): void {
    const sessionId = String(data?.sessionId || '');
    const blockedReason = this.getCreateBlockedReason();
    if (blockedReason) {
      this.reportCreateOutcome(requestId, { ok: false, isRefusal: true, message: blockedReason }, sessionId);
      return;
    }

    const target = this.intelliCatchItemContextService.resolveCreateTarget(sessionId);
    if (target?.refusal) {
      this.reportCreateOutcome(requestId, { ok: false, isRefusal: true, message: target.refusal }, sessionId);
      return;
    }

    if (!target?.itemContext) {
      this.createUnlinkedTests(suggestions, requestId);
      return;
    }

    this.createLinkedTests(suggestions, target.itemContext, requestId);
  }

  /** Blank when a Test can be created here; otherwise the sentence saying why it cannot. */
  private getCreateBlockedReason(): string {
    const availability = this.getHeaderChatAvailability();
    return availability.state === IntelliCatchAvailabilityState.Available ? '' : availability.reason;
  }

  /**
   * The availability answer for a chat with no item behind it. A blank node type is what tells the
   * shared rule to skip the parent-child check, which only applies to an action offered on an item.
   */
  private getHeaderChatAvailability(): IIntelliCatchAvailability {
    const availability = this.intelliCatchAvailabilityHelper?.getGenerateTestsAvailability('');
    const state = availability?.state ?? IntelliCatchAvailabilityState.NotApplicable;
    if (state === IntelliCatchAvailabilityState.Available) {
      return { state, reason: '' };
    }
    return { state, reason: (availability?.reason ?? '').trim() || UiMessageConstants.INTELLICATCH_TEST_TYPE_NOT_IN_TEMPLATE };
  }

  private postCreateAvailabilityIfOpen(): void {
    if (!this.isChatbotVisible) {
      return;
    }
    this.postCreateAvailability();
  }

  private postCreateAvailability(): void {
    const availability = this.getHeaderChatAvailability();
    const payload: IIntelliCatchCreateAvailabilityPayload = {
      canCreateTests: availability.state === IntelliCatchAvailabilityState.Available,
      reason: availability.reason,
    };
    this.postToChatWidget(IntelliCatchMessageAction.SetCreateAvailability, payload);
  }

  /**
   * Tells the user here and the widget there, so neither is left claiming a create that failed, and
   * puts anything that was not a success on the admin Logs screen: an Error for a failure, a Warning
   * for a refusal the app made itself. It is filed against the item the suggestions were for when
   * `sessionId` names one this conversation still knows; the header chat, and an answer whose session
   * has ended, have no item to file it against.
   */
  private reportCreateOutcome(requestId: string, report: IIntelliCatchCreateReport | null | undefined, sessionId = ''): void {
    const message = (report?.message ?? '').trim();
    if (message) {
      this.snackBar.open(message, undefined, { duration: CREATE_RESULT_SNACK_BAR_MS });
    }
    if (report?.ok !== true) {
      this.intelliCatchLogHelper.logCreateFailure(sessionId, message, report?.cause, report?.isRefusal ? CatchHubLogType.Warning : CatchHubLogType.Error);
    }

    const payload: IIntelliCatchCreateResultPayload = {
      requestId: (requestId ?? '').trim(),
      ok: report?.ok === true,
      reason: report?.ok === true ? '' : message,
    };
    this.postToChatWidget(IntelliCatchMessageAction.CreateResult, payload);
  }

  /**
   * Posts to the widget at the configured IntelliCatch origin, never '*': these messages carry what
   * the user may and may not do, and a wildcard would hand them to whatever the iframe now holds.
   */
  private postToChatWidget(action: IntelliCatchMessageAction, payload: unknown): void {
    const iframe = this.getChatIframe();
    if (!iframe?.contentWindow || !this.intelliCatchOrigin) {
      return;
    }

    iframe.contentWindow.postMessage({ action, payload }, this.intelliCatchOrigin);
    this.watchChatIframeLoad(iframe);
  }

  // The widget is reloaded whenever the window is closed and reopened, and a listener that is not
  // installed yet receives nothing, so the answer is sent again once the frame has loaded.
  private watchChatIframeLoad(iframe: HTMLIFrameElement): void {
    if (!iframe || this.watchedChatIframe === iframe) {
      return;
    }
    this.watchedChatIframe = iframe;
    iframe.addEventListener('load', this.resendOnChatIframeLoad);
  }

  private resendOnChatIframeLoad = (): void => {
    this.initializedOrganizationId = '';
    this.sendOrganizationIdToChatbot();
    this.postCreateAvailabilityIfOpen();
  };

  private toTestSuggestion(payload: any): IIntelliCatchTestSuggestion | null {
    const type = String(payload?.type || '').toLowerCase();
    const name = String(payload?.name || '').trim();
    if (type !== BluePrintConstants.TEST_NODE_LOWER || !name) {
      return null;
    }
    return {
      name,
      description: String(payload?.description || '').trim(),
      // The payload is untrusted postMessage data: entries that are not step-shaped objects are
      // dropped and every kept field is coerced, so nothing unstringifiable reaches a step column.
      steps: (Array.isArray(payload?.steps) ? payload.steps : [])
        .filter((step: unknown) => !!step && typeof step === 'object')
        .map((step: any, index: number) => ({
          stepNumber: Number(step.stepNumber) || index + 1,
          description: String(step.description ?? ''),
          expectedResult: String(step.expectedResult ?? ''),
        })),
    };
  }

  /**
   * Whether a window message really came from the embedded IntelliCatch widget.
   *
   * Both halves are needed. The origin alone would accept a message from any frame that had been
   * navigated to the app, and `source` alone would accept whatever that frame was navigated to
   * next; together they say the message came from this iframe while it was showing the configured
   * app. Anything else - another extension, an opener, a nested advert frame - is ignored.
   */
  private isMessageFromChatWidget(event: MessageEvent): boolean {
    if (!event?.origin || !this.intelliCatchOrigin || event.origin.toLowerCase() !== this.intelliCatchOrigin.toLowerCase()) {
      return false;
    }
    const iframe = this.getChatIframe();
    return !!iframe?.contentWindow && event.source === iframe.contentWindow;
  }

  private getChatIframe(): HTMLIFrameElement | null {
    return document.querySelector(CHAT_IFRAME_SELECTOR) as HTMLIFrameElement | null;
  }

  private toOrigin(url: string | null | undefined): string {
    if (!url) {
      return '';
    }
    try {
      const origin = new URL(url, window.location.href).origin;
      return origin && origin.toLowerCase() !== 'null' ? origin : '';
    } catch {
      return '';
    }
  }

  private debouncedRefresh(): void {
    // Clear existing timer if user clicks again
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    // Set new timer - refresh only after user stops clicking for 2 seconds
    this.refreshTimer = setTimeout(() => {
      this.refreshWorkspaceView();
      this.refreshTimer = null;
    }, 2000); // 2 second cooldown period
  }

  private refreshWorkspaceView(): void {
    try {
      this.workspaceStore.$selectedWorkspaceInstance
        .subscribe((selectedWorkspace) => {
          if (this.workspaceStore && selectedWorkspace) {
            this.workspaceStore.$getWorkspaceById(selectedWorkspace.id).subscribe({
              next: (updatedWorkspace) => {
                if (updatedWorkspace) {
                  // The workspace store automatically sets selectedWorkspaceInstance
                  // loadNodesForWorkspace() will be called automatically via the subscription
                  this.loggerService.traceInfo(this.className, 'refreshWorkspaceView', 'Workspace view refreshed after cooldown', '', null);
                }
              },
              error: (error) => {
                this.loggerService.traceInfo(this.className, 'refreshWorkspaceView', 'Failed to refresh workspace view', '', error);
              },
            });
          }
        })
        .unsubscribe();
    } catch (error) {
      this.loggerService.traceInfo(this.className, 'refreshWorkspaceView', 'Error during workspace refresh', '', error);
    }
  }

  private generateRandomLocation(): string {
    const randomX = Math.floor(Math.random() * 501) - 100;

    const randomY = Math.floor(Math.random() * 501) - 100;

    return `${randomX} ${randomY}`;
  }

  /**
   * The header chat has no item to hang a Test off, so it creates loose ones wherever they land.
   *
   * Each create is subscribed to here rather than left to the helper's shared subscription slot, and
   * the user is told what happened once they have all come back - never while they are still in
   * flight, which is what let a create that saved nothing read as a success.
   */
  private createUnlinkedTests(suggestions: IIntelliCatchTestSuggestion[], requestId: string): void {
    const requested = Array.isArray(suggestions) ? suggestions.filter((suggestion) => !!suggestion?.name?.trim()) : [];
    if (requested.length === 0) {
      this.reportCreateOutcome(requestId, { ok: false, isRefusal: true, message: UiMessageConstants.INTELLICATCH_CREATE_NOTHING_TO_CREATE });
      return;
    }

    const results: IIntelliCatchUnlinkedCreateResult[] = [];
    requested.forEach((suggestion) => {
      const name = suggestion.name.trim();
      let failureReason = '';

      this.nodeHelper
        .$createNodeWithFieldValues(
          BluePrintConstants.TEST_NODE_LOWER,
          name,
          this.generateRandomLocation(),
          suggestion.description ?? '',
          this.buildTestFieldValues(name, suggestion.steps),
          NO_PARENT_NODE_ID,
        )
        .pipe(
          // One settled emission per create: a repeat must not count as another result, and a
          // silent completion must not leave the widget's requestId unanswered.
          take(1),
          defaultIfEmpty(null),
          catchError((err) => {
            console.error('Failed to create Test item from IntelliCatch:', err);
            failureReason = UiMessageConstants.intelliCatchCreateErrorReason(err);
            return of(null);
          }),
          takeUntil(this.destroyed),
        )
        .subscribe((createdNode) => {
          const created = !!createdNode?.id;
          results.push({
            name,
            created,
            node: createdNode ?? null,
            reason: created ? '' : failureReason || UiMessageConstants.INTELLICATCH_CREATE_REASON_NOT_CREATED,
          });
          if (results.length < requested.length) {
            return;
          }

          // The panel follows the last test that actually got created, not the last one asked for,
          // and the active filter still decides whether it opens or closes.
          const lastCreated = [...results].reverse().find((result) => result.created);
          if (lastCreated?.node) {
            this.nodeHelper.openEditPanelForNode(lastCreated.node);
          }
          this.reportCreateOutcome(requestId, this.buildUnlinkedCreateReport(results));
          this.debouncedRefresh();
        });
    });
  }

  private buildUnlinkedCreateReport(results: IIntelliCatchUnlinkedCreateResult[]): IIntelliCatchCreateReport {
    const outcomes = Array.isArray(results) ? results.filter((result) => !!result) : [];
    if (outcomes.length === 0) {
      return { ok: false, isRefusal: true, message: UiMessageConstants.INTELLICATCH_CREATE_NOTHING_TO_CREATE };
    }

    const created = outcomes.filter((result) => result.created);
    const ok = created.length === outcomes.length;
    const cause = UiMessageConstants.intelliCatchFailureCause(
      outcomes.filter((result) => !result.created),
      outcomes.length,
    );

    if (outcomes.length === 1) {
      return {
        ok,
        message: ok ? UiMessageConstants.intelliCatchTestCreatedUnlinkedMessage(outcomes[0].name) : UiMessageConstants.intelliCatchTestNotCreatedMessage(outcomes[0].name),
        cause,
      };
    }

    return {
      ok,
      message: ok
        ? UiMessageConstants.intelliCatchTestsCreatedUnlinkedMessage(created.length)
        : UiMessageConstants.intelliCatchTestsPartiallyCreatedMessage(created.map((result) => result.name), outcomes.length),
      cause,
    };
  }

  /**
   * Queues every suggestion for sequential create + link and reports once all results are back, so a
   * failed or unlinked create is never hidden behind a successful one.
   */
  private createLinkedTests(suggestions: IIntelliCatchTestSuggestion[], itemContext: IIntelliCatchItemContext | null | undefined, requestId: string): void {
    const requested = Array.isArray(suggestions) ? suggestions.filter((suggestion) => !!suggestion?.name?.trim()) : [];
    if (requested.length === 0 || !itemContext?.nodeId) {
      this.reportCreateOutcome(requestId, { ok: false, isRefusal: true, message: UiMessageConstants.INTELLICATCH_CREATE_NOTHING_TO_CREATE }, itemContext?.sessionId ?? '');
      return;
    }

    // Every request is built before the first one is queued: a throw part-way through the loop
    // would leave some suggestions queued and the rest never sent, with no report for any of them.
    let createRequests: IIntelliCatchTestCreateRequest[];
    try {
      createRequests = requested.map((suggestion, index) => ({
        name: suggestion.name,
        description: suggestion.description,
        fieldValues: this.buildTestFieldValues(suggestion.name, suggestion.steps),
        parentNodeId: itemContext.nodeId,
        isOpenEditPanel: index === requested.length - 1,
      }));
    } catch (err) {
      console.error('IntelliCatch: failed to prepare the suggested tests.', err);
      this.reportCreateOutcome(
        requestId,
        {
          ok: false,
          message: UiMessageConstants.intelliCatchTestsPartiallyCreatedMessage([], requested.length),
          cause: UiMessageConstants.INTELLICATCH_CREATE_REASON_PREPARE_FAILED,
        },
        itemContext.sessionId,
      );
      return;
    }

    const results: IIntelliCatchTestCreateResult[] = [];
    createRequests.forEach((createRequest) => {
      this.intelliCatchLinkedTestHelper
        .queueCreate(createRequest)
        .pipe(takeUntil(this.destroyed))
        .subscribe((result) => {
          results.push(result);
          if (results.length < requested.length) {
            return;
          }
          this.reportCreateOutcome(requestId, this.buildLinkedCreateReport(results, itemContext), itemContext.sessionId);
          this.debouncedRefresh();
        });
    });
  }

  private buildLinkedCreateReport(results: IIntelliCatchTestCreateResult[], itemContext: IIntelliCatchItemContext): IIntelliCatchCreateReport {
    const outcomes = Array.isArray(results) ? results.filter((result) => !!result) : [];
    if (outcomes.length === 0) {
      return { ok: false, isRefusal: true, message: UiMessageConstants.INTELLICATCH_CREATE_NOTHING_TO_CREATE };
    }

    const key = itemContext?.key;
    const summary = itemContext?.summary;
    const created = outcomes.filter(
      (result) => result.outcome === IntelliCatchTestCreateOutcome.CreatedAndLinked || result.outcome === IntelliCatchTestCreateOutcome.CreatedNotLinked,
    );
    const notLinked = outcomes.filter((result) => result.outcome === IntelliCatchTestCreateOutcome.CreatedNotLinked);

    // A Test that was created but left unlinked is not a success: the user asked for tests under
    // their item, and one sitting loose on the Map is not that.
    const ok = outcomes.every((result) => result.outcome === IntelliCatchTestCreateOutcome.CreatedAndLinked);
    const cause = UiMessageConstants.intelliCatchFailureCause(
      outcomes.filter((result) => result.outcome !== IntelliCatchTestCreateOutcome.CreatedAndLinked),
      outcomes.length,
    );
    // Every parent gone means nothing was attempted: the user's own doing, not a failure to act on.
    const isRefusal = outcomes.every((result) => result.outcome === IntelliCatchTestCreateOutcome.ParentMissing);

    if (outcomes.length === 1) {
      return { ok, isRefusal, message: this.singleCreateResultMessage(outcomes[0], itemContext), cause };
    }

    const messages: string[] = [];
    if (ok) {
      messages.push(UiMessageConstants.intelliCatchTestsCreatedMessage(created.length, key, summary));
    } else if (isRefusal) {
      messages.push(UiMessageConstants.intelliCatchParentMissingMessage(key, summary));
    } else {
      messages.push(UiMessageConstants.intelliCatchTestsPartiallyCreatedMessage(created.map((result) => result.name), outcomes.length));
      if (notLinked.length > 0) {
        messages.push(UiMessageConstants.intelliCatchTestsNotLinkedMessage(notLinked.map((result) => result.name), key, summary));
      }
    }

    return { ok, isRefusal, message: messages.join(' '), cause };
  }

  private singleCreateResultMessage(result: IIntelliCatchTestCreateResult, itemContext: IIntelliCatchItemContext): string {
    const key = itemContext?.key;
    const summary = itemContext?.summary;
    switch (result?.outcome) {
      case IntelliCatchTestCreateOutcome.CreatedAndLinked:
        return UiMessageConstants.intelliCatchTestCreatedMessage(result.name, key, summary);
      case IntelliCatchTestCreateOutcome.CreatedNotLinked:
        return UiMessageConstants.intelliCatchTestNotLinkedMessage(result.name, key, summary);
      case IntelliCatchTestCreateOutcome.ParentMissing:
        return UiMessageConstants.intelliCatchParentMissingMessage(key, summary);
      default:
        return UiMessageConstants.intelliCatchTestNotCreatedMessage(result?.name);
    }
  }

  /**
   * Field ids come from the Test item type's own assigned fields, not from a name search across the
   * whole template, which can land on a field no Test carries.
   */
  private buildTestFieldValues(name: string, steps: IIntelliCatchTestStepInput[] | null | undefined): { [fieldId: string]: any } {
    const fieldValues: { [fieldId: string]: any } = {};
    const testFieldIds = this.intelliCatchAvailabilityHelper.getTestFieldIds();

    if (testFieldIds?.sourceFieldId) {
      fieldValues[testFieldIds.sourceFieldId] = BluePrintConstants.FIELD_VALUE_SOURCE_AI;
    }

    const assigneeFieldValue = this.resolveIntellicatchAssigneeValue(name, testFieldIds?.assigneeFieldId);
    if (testFieldIds?.assigneeFieldId) {
      fieldValues[testFieldIds.assigneeFieldId] = assigneeFieldValue;
    }

    const stepRows = this.buildTestStepRows(steps);
    if (stepRows.length > 0 && testFieldIds?.stepsTableFieldId) {
      fieldValues[testFieldIds.stepsTableFieldId] = stepRows;
    }

    return fieldValues;
  }

  private buildTestStepRows(steps: IIntelliCatchTestStepInput[] | null | undefined): ITestTableRowModel[] {
    if (!Array.isArray(steps) || steps.length === 0) {
      return [];
    }

    const defaultStepStatus = this.editPanelTestStepHelper.getDefaultStepStatus(this.workspaceStore.selectedWorkspaceInstance);
    return steps.map((step, index) => ({
      id: index + 1,
      description: step?.description || '',
      expectedResult: step?.expectedResult || '',
      data: '',
      notes: '',
      actualResult: '',
      attachments: [],
      teststatus: defaultStepStatus,
      bug: [],
    }));
  }

  private resolveIntellicatchAssigneeValue(nodeName: string, assigneeFieldId: string | null | undefined): string {
    if (!assigneeFieldId) {
      this.logIntellicatchUnassigned(nodeName, 'The "Assigned To" field is not configured in the selected workspace.');
      return '';
    }

    const currentUserId = this.userService.getCurrentUser()?.id || this.currentUserId;
    if (!currentUserId) {
      this.logIntellicatchUnassigned(nodeName, 'The current user could not be resolved.');
      return '';
    }

    const assignableUsers = this.getAssignableUsers();
    if (assignableUsers.length === 0) {
      this.logIntellicatchUnassigned(nodeName, 'No assignable users are currently available in this workspace.');
      return '';
    }

    const currentUser = assignableUsers.find((user) => user?.id === currentUserId);
    if (!currentUser) {
      const currentUserLabel = this.getCurrentUserLabel();
      this.logIntellicatchUnassigned(nodeName, `Current user "${currentUserLabel}" is not available in the "Assigned To" user list.`);
      return '';
    }

    return currentUser.id;
  }

  private getAssignableUsers(): UserModel[] {
    return this.assignableProjectUsers.filter((user) => !!user && user.isActive && !user.isArchived);
  }

  private getCurrentUserLabel(): string {
    const currentUser = this.userService.getCurrentUser();
    return currentUser?.emailAddress || currentUser?.userName || currentUser?.displayName || this.currentUserId || 'Unknown User';
  }

  private logIntellicatchUnassigned(nodeName: string, reason: string): void {
    const message = `IntelliCatch created Test item "${nodeName}" as Unassigned. ${reason}`;
    const currentUser = this.userService.getCurrentUser();
    const currentProject = this.projectStore.selectedProject;
    const timestamp = new Date().toISOString();
    const userId = currentUser?.id || this.currentUserId || this.userStore.logUser?.id || 'Unknown User';
    const userName = currentUser?.emailAddress || currentUser?.userName || this.userStore.logUser?.emailAddress || 'Unknown User';

    this.loggerService.logWarning(this.className, 'resolveIntellicatchAssigneeValue', message, '', {
      nodeName,
      currentUserId: this.currentUserId,
      currentUser: this.getCurrentUserLabel(),
      workspaceId: this.workspaceStore.selectedWorkspaceInstance?.id,
    });

    this.catchHubLogStore.addLogs([
      new CatchHubLogModel(
        this.idHelper.generateNewGUID24(),
        message,
        timestamp,
        timestamp,
        userId,
        userId,
        currentProject?.name || '',
        currentProject?.id || '',
        'IntelliCatch',
        '',
        '',
        'info',
        userName,
      ),
    ]);
  }

  /**
   * Records a Copilot request Microsoft refused for a tenant-side reason. Graph's own error code is
   * kept in the row: Microsoft publishes no distinct code for an exhausted Copilot quota, so the
   * string it actually sends is worth having on the record rather than inferred from the status.
   */
  private logCopilotLimit(payload: { code?: string; message?: string; upstreamError?: string }): void {
    const message = String(payload?.message || '').trim();
    if (!message) {
      return;
    }
    const currentUser = this.userService.getCurrentUser();
    const currentProject = this.projectStore.selectedProject;
    const timestamp = new Date().toISOString();
    const userId = currentUser?.id || this.currentUserId || this.userStore.logUser?.id || 'Unknown User';
    const userName = currentUser?.emailAddress || currentUser?.userName || this.userStore.logUser?.emailAddress || 'Unknown User';
    const upstream = String(payload?.upstreamError || '').trim();

    this.catchHubLogStore.addLogs([
      new CatchHubLogModel(
        this.idHelper.generateNewGUID24(),
        upstream ? `${message} (Microsoft reported: ${upstream})` : message,
        timestamp,
        timestamp,
        userId,
        userId,
        currentProject?.name || '',
        currentProject?.id || '',
        CatchHubEventType.CopilotLimit,
        String(payload?.code || ''),
        '',
        CatchHubLogType.Error,
        userName,
      ),
    ]);
  }

  /**
   * Records a chat request the service refused over its own missing configuration - env vars, an
   * API key, connectivity settings - for whichever AI provider it needed. Not caused by the user or
   * their tenant, so an administrator needs this on the record to find and fix it.
   */
  private logServiceConfigurationError(payload: { code?: string; message?: string }): void {
    const message = String(payload?.message || '').trim();
    if (!message) {
      return;
    }
    const currentUser = this.userService.getCurrentUser();
    const currentProject = this.projectStore.selectedProject;
    const timestamp = new Date().toISOString();
    const userId = currentUser?.id || this.currentUserId || this.userStore.logUser?.id || 'Unknown User';
    const userName = currentUser?.emailAddress || currentUser?.userName || this.userStore.logUser?.emailAddress || 'Unknown User';

    this.catchHubLogStore.addLogs([
      new CatchHubLogModel(
        this.idHelper.generateNewGUID24(),
        message,
        timestamp,
        timestamp,
        userId,
        userId,
        currentProject?.name || '',
        currentProject?.id || '',
        CatchHubEventType.ConfigurationError,
        String(payload?.code || ''),
        '',
        CatchHubLogType.Error,
        userName,
      ),
    ]);
  }

  private closeChatbot(): void {
    //this.isChatbotVisible = false;
    this.toolbarEventManagerHelper.CommonToolbarIntelliCatchWindowOpen = false;
    this.isChatbotMaximized = false;

    // Send close message to iframe
    const iframe = this.getChatIframe();
    if (iframe?.contentWindow) {
      iframe.contentWindow.postMessage({ action: 'close', reason: 'navigation' }, '*');
    }

    // Force fresh instance on next open
    const currentUrl = this.chatbotUrl;
    this.chatbotUrl = this.sanitizer.bypassSecurityTrustResourceUrl(BLANK_FRAME_URL);
    setTimeout(() => {
      this.chatbotUrl = currentUrl;
      this.cdr.detectChanges();
    }, 50);

    this.cdr.detectChanges();
  }

  /**
   * Tells the widget its organization, and checks for a Copilot token, once per widget document:
   * the workspace and blueprint streams re-emit on every item create and edit, and each repeat would
   * otherwise make the widget re-read its provider and the host re-read the AI config.
   */
  private sendOrganizationIdToChatbot(): void {
    const organizationId = this.organizationId;
    const iframe = this.getChatIframe();
    if (!organizationId || !iframe?.contentWindow || !this.isShowingWidget(iframe)) {
      return;
    }

    this.watchChatIframeLoad(iframe);
    if (this.initializedOrganizationId === organizationId) {
      return;
    }
    this.initializedOrganizationId = organizationId;

    iframe.contentWindow.postMessage({ action: IntelliCatchMessageAction.Initialize, organizationId }, '*');
    this.maybeSendCopilotToken();
  }

  private isShowingWidget(iframe: HTMLIFrameElement | null): boolean {
    const src = (iframe?.getAttribute('src') ?? '').trim().toLowerCase();
    return !!src && src !== BLANK_FRAME_URL;
  }

  /**
   * If this org is configured for M365 Copilot, silently check for an existing Microsoft session and
   * post the token to the chat widget; otherwise set requiresCopilotSignIn so the banner can prompt a
   * manual sign-in. No-op when Copilot MSAL config is blank (tryGetTokenSilent returns null).
   */
  private maybeSendCopilotToken(): void {
    this.requiresCopilotSignIn = false;
    // Orgs without an active IntelliCatch app have no AI config to fetch, and asking for one 404s.
    if (!this.organizationId || !this.isIntelliCatchAvailable) {
      return;
    }
    this.externalLinkService.getAiConfig(this.organizationId).subscribe({
      next: (cfg) => {
        if (cfg?.providerType?.toLowerCase() !== AiProvider.M365Copilot) {
          return;
        }
        // A Microsoft sign-in popup can only be opened from a user gesture, so on open we only try
        // for a token silently (returning users). If none, prompt the user to click Sign in.
        this.copilotAuthService
          .tryGetTokenSilent()
          .then((token) => {
            if (token) {
              this.postCopilotToken(token);
            } else {
              this.requiresCopilotSignIn = true;
            }
            this.cdr.detectChanges();
          })
          .catch((err) => {
            console.warn('Copilot silent token check failed', err);
            this.requiresCopilotSignIn = true;
            this.cdr.detectChanges();
          });
      },
      error: (err) => {
        // Expected for an org with no AI config / no read permission; log so a real backend outage
        // is not silently indistinguishable from that.
        console.warn('Could not load AI config for Copilot check', err);
      },
    });
  }

  /**
   * Records the outcome of an interactive Microsoft sign-in on the admin Logs screen. Only the
   * click-driven sign-in is recorded: the silent token check that runs whenever the widget opens
   * would otherwise write a row every time a returning user opened the chat.
   */
  private logCopilotSignIn(succeeded: boolean, error?: string): void {
    const currentUser = this.userService.getCurrentUser();
    const currentProject = this.projectStore.selectedProject;
    const timestamp = new Date().toISOString();
    const userId = currentUser?.id || this.currentUserId || this.userStore.logUser?.id || 'Unknown User';
    const userName = currentUser?.emailAddress || currentUser?.userName || this.userStore.logUser?.emailAddress || 'Unknown User';
    // The Frankly user (above) and the Microsoft account are separate logins that need not match -
    // worth recording together rather than assumed to be the same person.
    const msAccountSuffix = this.copilotAuthService.getSignedInUsername()
      ? ` (Microsoft account: ${this.copilotAuthService.getSignedInUsername()})`
      : '';
    const message = succeeded
      ? `Microsoft 365 Copilot signin successful${msAccountSuffix}`
      : `Microsoft 365 Copilot signin is not successful. And the error is: ${error || 'unknown error'}${msAccountSuffix}`;

    this.catchHubLogStore.addLogs([
      new CatchHubLogModel(
        this.idHelper.generateNewGUID24(),
        message,
        timestamp,
        timestamp,
        userId,
        userId,
        currentProject?.name || '',
        currentProject?.id || '',
        CatchHubEventType.CopilotSignIn,
        '',
        '',
        succeeded ? CatchHubLogType.Info : CatchHubLogType.Error,
        userName,
      ),
    ]);
  }

  /**
   * Hand a fresh token to the widget, or put the sign-in banner back when there isn't one. The
   * banner is the only route back in: it covers the iframe, so without it a signed-out user can
   * still type and every message fails against a missing token.
   */
  private applyCopilotSignInState(token: string | null): void {
    if (token) {
      this.postCopilotToken(token);
      this.requiresCopilotSignIn = false;
    } else {
      this.requiresCopilotSignIn = true;
    }
    this.copilotSignInBusy = false;
    this.copilotSignInError = '';
    this.cdr.detectChanges();
  }

  /** Triggered by a user click on the parent sign-in banner, so the MSAL popup is allowed to open. */
  signInToCopilot(): void {
    const attempt = ++this.copilotSignInAttempt;
    this.copilotSignInError = '';
    this.copilotSignInBusy = true;
    this.cdr.detectChanges();

    // Closing the Microsoft window leaves MSAL waiting for a broadcast that will never arrive, so
    // the promise below can stay pending. Release the button ourselves rather than stranding it.
    const watchdog = setTimeout(() => {
      if (attempt !== this.copilotSignInAttempt || !this.copilotSignInBusy) {
        return;
      }
      this.copilotSignInBusy = false;
      this.copilotSignInError = 'Sign-in did not finish. If you closed the Microsoft window, try again.';
      this.logCopilotSignIn(false, this.copilotSignInError);
      this.cdr.detectChanges();
    }, COPILOT_SIGN_IN_WATCHDOG_MS);

    this.copilotAuthService
      .signInAndGetApiToken()
      .then((token) => {
        // A newer attempt is in flight; its result is the one that counts. A late success from this
        // attempt is still honoured, so a slow MFA sign-in is not thrown away by the watchdog.
        if (attempt !== this.copilotSignInAttempt) {
          return;
        }
        if (!token) {
          this.copilotSignInError = 'Copilot sign-in is not configured.';
          this.logCopilotSignIn(false, this.copilotSignInError);
          return;
        }
        this.postCopilotToken(token);
        this.requiresCopilotSignIn = false;
        this.copilotSignInError = '';
        this.logCopilotSignIn(true);
      })
      .catch((err) => {
        if (attempt !== this.copilotSignInAttempt) {
          return;
        }
        // Closing the popup is a deliberate act, not a fault - say so plainly instead of showing an MSAL code.
        if (err?.errorCode === 'user_cancelled') {
          this.copilotSignInError = 'Sign-in was cancelled.';
          this.logCopilotSignIn(false, this.copilotSignInError);
          return;
        }
        // Surface the real reason so it can be acted on (popup blocked, AADSTS, stuck interaction, etc.).
        const code = err?.errorCode ? `[${err.errorCode}] ` : '';
        this.copilotSignInError = `Sign-in failed: ${code}${err?.errorMessage || err?.message || 'unknown error'}`;
        this.logCopilotSignIn(false, this.copilotSignInError);
        console.error('Copilot sign-in failed', err);
      })
      .finally(() => {
        clearTimeout(watchdog);
        if (attempt !== this.copilotSignInAttempt) {
          return;
        }
        this.copilotSignInBusy = false;
        this.cdr.detectChanges();
      });
  }

  private postCopilotToken(token: string): void {
    const iframe = this.getChatIframe();
    if (!iframe?.contentWindow) {
      console.warn('Copilot token not delivered: chat iframe not ready');
      return;
    }
    iframe.contentWindow.postMessage(
      { app: 'CatchHub', type: environment.ParentChildAppCommunicationType.copilotToken, data: { accessToken: token } },
      '*',
    );
  }

  private handleWindowResize = (): void => {
    if (this.isChatbotVisible && !this.isChatbotMaximized) {
      // Adjust position to keep chatbot within viewport
      this.chatbotPosition = {
        x: Math.min(this.chatbotPosition.x, window.innerWidth - 470),
        y: Math.min(this.chatbotPosition.y, window.innerHeight - 700),
      };
      this.chatbotPosition = {
        x: Math.max(0, this.chatbotPosition.x),
        y: Math.max(0, this.chatbotPosition.y),
      };
      this.cdr.detectChanges();
    }
  };
}
